/**
 * App Update Manager
 * Orchestrates all PWA update logic:
 *
 * 1. Fresh app open: Auto-reload if SW update available
 * 2. Return from background: Check API version, reload if critically outdated
 * 3. Hourly interval: Silent SW check (queued for next fresh open)
 *
 * This component renders nothing - all reloads are automatic
 */

import { useEffect, useRef } from 'react'
import { usePWAUpdate } from '@/hooks/usePWAUpdate'
import { useVersionCheck } from '@/hooks/useVersionCheck'

/**
 * Component that manages app updates automatically
 *
 * Mount this at the root of your app (inside providers) to enable:
 * - Auto-reload on fresh app open when SW update is available
 * - Version check on visibility change (return from background)
 *
 * @example
 * ```tsx
 * // In __root.tsx or App.tsx
 * function RootComponent() {
 *   return (
 *     <Providers>
 *       <AppUpdateManager />
 *       <Outlet />
 *     </Providers>
 *   )
 * }
 * ```
 */
export function AppUpdateManager() {
    const { needRefresh, updateAndReload } = usePWAUpdate()
    const { checkVersion } = useVersionCheck()
    const isFirstLoad = useRef(true)
    const hasHandledInitialUpdate = useRef(false)

    // 1. Fresh app open: auto-reload if SW update available
    useEffect(() => {
        // Only trigger on first load when needRefresh becomes true
        if (isFirstLoad.current && needRefresh && !hasHandledInitialUpdate.current) {
            console.log('[AppUpdateManager] SW update detected on fresh open, reloading...')
            hasHandledInitialUpdate.current = true
            updateAndReload()
        }
    }, [needRefresh, updateAndReload])

    // 1b. Initial load: check API version after SW check completes
    useEffect(() => {
        // Small delay to let SW check complete first, but before marking first load as complete
        const timer = setTimeout(async () => {
            console.log('[AppUpdateManager] Initial load, checking API version...')
            const outdated = await checkVersion()

            if (outdated) {
                console.log('[AppUpdateManager] Frontend outdated per API on initial load, reloading...')
                window.location.reload()
            }
        }, 800) // Before isFirstLoad is marked false (1s), but after SW check

        return () => clearTimeout(timer)
    }, [checkVersion])

    // Mark first load as complete after initial render
    useEffect(() => {
        // Small delay to ensure we've checked for initial SW update and API version
        const timer = setTimeout(() => {
            isFirstLoad.current = false
        }, 1000)

        return () => clearTimeout(timer)
    }, [])

    // 2. Visibility change: check API version only (not SW)
    // Note: checkVersion is memoized with useCallback([]) in useVersionCheck,
    // so it's stable and won't cause unnecessary effect re-runs
    useEffect(() => {
        const handleVisibilityChange = async () => {
            // Only check when becoming visible AND not on first load
            if (document.visibilityState === 'visible' && !isFirstLoad.current) {
                console.log('[AppUpdateManager] App became visible, checking API version...')

                const outdated = await checkVersion()

                if (outdated) {
                    console.log('[AppUpdateManager] Frontend outdated per API, reloading...')
                    window.location.reload()
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [checkVersion])

    // This component doesn't render anything - all updates are automatic
    return null
}

export default AppUpdateManager

